import React from "react";
import { FaCheck, FaDumbbell, FaTimes, FaUserFriends } from "react-icons/fa";

import {
  DEFAULT_AVATAR,
  getAdherentName,
  getAdherentSubtitle,
} from "../utils/preparationPhysiqueUtils";

function FieldLabel({ children }) {
  return (
    <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
      {children}
    </span>
  );
}

function AssignmentPanel({
  groups,
  adherentsByGroup,
  selectedExercise,
  form,
  selectedAdherentIds,
  assigning,
  onChange,
  onToggleAdherent,
  onToggleGroup,
  onClearSelection,
  onSubmit,
}) {
  return (
    <article className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-200">
          Attribution
        </p>

        <h2 className="mt-2 font-titre text-2xl font-bold text-slate-950 dark:text-white">
          Attribuer aux judokas
        </h2>

        <p className="mt-2 text-sm font-medium leading-6 text-slate-500 dark:text-white/50">
          Sélectionne un exercice dans le catalogue, puis coche un groupe ou
          plusieurs judokas.
        </p>
      </div>

      <div className="mt-5 rounded-[1.5rem] border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/20 dark:bg-emerald-500/10">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-emerald-700 shadow-sm dark:bg-white/10 dark:text-emerald-200">
            <FaDumbbell />
          </div>

          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-200">
              Exercice sélectionné
            </p>

            <p className="truncate text-sm font-bold text-slate-950 dark:text-white">
              {selectedExercise?.nom || "Aucun exercice sélectionné"}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={onSubmit} className="mt-5 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <FieldLabel>Date prévue</FieldLabel>

            <input
              type="date"
              name="date_realisation"
              value={form.date_realisation}
              onChange={onChange}
              className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-emerald-300 focus:ring-4 focus:ring-emerald-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-emerald-300/50 dark:focus:ring-emerald-500/10"
            />
          </label>

          <label className="block">
            <FieldLabel>Répétitions / objectif</FieldLabel>

            <input
              type="number"
              name="repetitions"
              min="0"
              value={form.repetitions}
              onChange={onChange}
              placeholder="Ex : 30"
              className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-emerald-300 focus:ring-4 focus:ring-emerald-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-emerald-300/50 dark:focus:ring-emerald-500/10"
            />
          </label>
        </div>

        <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.04]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Sélection
              </p>

              <p className="text-sm font-bold text-slate-800 dark:text-white">
                {selectedAdherentIds.length} judoka
                {selectedAdherentIds.length > 1 ? "s" : ""} sélectionné
                {selectedAdherentIds.length > 1 ? "s" : ""}
              </p>
            </div>

            {selectedAdherentIds.length > 0 && (
              <button
                type="button"
                onClick={onClearSelection}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
              >
                <FaTimes />
                Vider
              </button>
            )}
          </div>
        </div>

        {groups.length === 0 ? (
          <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-white/10 dark:bg-white/[0.03]">
            <FaUserFriends className="mx-auto text-2xl text-slate-400" />

            <p className="mt-3 text-sm font-bold text-slate-700 dark:text-white">
              Aucun groupe disponible.
            </p>
          </div>
        ) : (
          <div className="max-h-[520px] space-y-3 overflow-y-auto pr-1">
            {groups.map((group) => {
              const adherents = adherentsByGroup[group.id] || [];

              const groupIds = adherents.map((adherent) =>
                Number(adherent.id),
              );

              const allGroupSelected =
                groupIds.length > 0 &&
                groupIds.every((id) => selectedAdherentIds.includes(id));

              return (
                <div
                  key={group.id}
                  className="rounded-[1.5rem] border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-950"
                >
                  <button
                    type="button"
                    onClick={() => onToggleGroup(group.id)}
                    className={`flex min-h-12 w-full items-center justify-between gap-3 rounded-2xl px-3 py-2 text-left transition ${
                      allGroupSelected
                        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200"
                        : "bg-slate-50 text-slate-700 hover:bg-slate-100 dark:bg-white/[0.05] dark:text-white dark:hover:bg-white/[0.08]"
                    }`}
                  >
                    <span>
                      <span className="block text-sm font-bold">
                        {group.nom}
                      </span>

                      <span className="block text-xs font-semibold opacity-70">
                        {adherents.length} judoka
                        {adherents.length > 1 ? "s" : ""}
                      </span>
                    </span>

                    {allGroupSelected && <FaCheck className="shrink-0" />}
                  </button>

                  {adherents.length > 0 && (
                    <div className="mt-3 grid gap-2">
                      {adherents.map((adherent) => {
                        const isSelected = selectedAdherentIds.includes(
                          Number(adherent.id),
                        );

                        return (
                          <button
                            key={adherent.id}
                            type="button"
                            onClick={() => onToggleAdherent(adherent.id)}
                            className={`flex items-center gap-3 rounded-2xl border p-3 text-left transition ${
                              isSelected
                                ? "border-emerald-300 bg-emerald-50 dark:border-emerald-400/30 dark:bg-emerald-500/10"
                                : "border-slate-200 bg-slate-50 hover:bg-white dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
                            }`}
                          >
                            <img
                              src={adherent.photo_url || DEFAULT_AVATAR}
                              alt={getAdherentName(adherent)}
                              className="h-11 w-11 rounded-2xl object-cover"
                              onError={(event) => {
                                event.currentTarget.src = DEFAULT_AVATAR;
                              }}
                            />

                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-bold text-slate-950 dark:text-white">
                                {getAdherentName(adherent)}
                              </span>

                              <span className="block truncate text-xs font-semibold text-slate-500 dark:text-white/50">
                                {getAdherentSubtitle(adherent)}
                              </span>
                            </span>

                            {isSelected && (
                              <FaCheck className="shrink-0 text-emerald-600" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <button
          type="submit"
          disabled={assigning || !selectedExercise || !selectedAdherentIds.length}
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
        >
          <FaCheck />
          {assigning ? "Attribution..." : "Attribuer l'exercice"}
        </button>
      </form>
    </article>
  );
}

export default AssignmentPanel;